// /Users/donovan68/dva-frontend-vite/src/hooks/useProspectList.ts
// Custom hook for managing prospect list state across Pro journey pages
// Fetches use case statuses and joins with full use case metadata

import { useState, useEffect, useCallback } from 'react';

// Type definitions
interface UseCaseStatus {
  use_case_id: number;
  status: string;
  updated_at: string;
  updated_by: string;
}

interface UseCaseMeta {
  use_case_id: number;
  name: string;
  use_case_abr?: string;
  business_value_score?: number;
  implementation_score?: number;
  category_name?: string;
  annual_revenue_potential?: number;
  payback_months?: number;
  confidence_level?: string;
  status: string;
}

interface ProspectListHook {
  prospects: UseCaseMeta[];
  statuses: UseCaseStatus[];
  isLoading: boolean;
  error: string | null;
  updateStatus: (useCaseId: number, status: string) => Promise<void>;
  refetch: () => Promise<void>;
}

const API_BASE = `${import.meta.env.VITE_BACKEND_URL}/api/v2`;

export function useProspectList(clientId: string): ProspectListHook {
  const [statuses, setStatuses] = useState<UseCaseStatus[]>([]);
  const [prospects, setProspects] = useState<UseCaseMeta[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all statuses for this client
  const fetchStatuses = useCallback(async () => {
    if (!clientId) {
      console.warn('useProspectList: No clientId provided');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log(`📡 [useProspectList] Fetching statuses for client ${clientId}...`);
      
      const response = await fetch(`${API_BASE}/usecases/${clientId}/statuses`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch statuses: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error('API returned unsuccessful response');
      }

      const allStatuses = data.statuses || [];
      console.log(`✅ [useProspectList] Fetched ${allStatuses.length} statuses`);
      console.log(`📋 [useProspectList] All statuses:`, allStatuses.map((s: UseCaseStatus) => ({
        use_case_id: s.use_case_id,
        status: s.status
      })));
      setStatuses(allStatuses);

      // Filter for prospects and fetch their details
      const prospectStatuses = allStatuses.filter((s: UseCaseStatus) => s.status === 'prospect');
      console.log(`📋 [useProspectList] Found ${prospectStatuses.length} prospects with IDs:`, prospectStatuses.map((s: UseCaseStatus) => s.use_case_id));
      
      if (prospectStatuses.length > 0) {
        console.log(`📋 [useProspectList] Fetching details for prospect IDs:`, prospectStatuses.map((s: UseCaseStatus) => s.use_case_id));
        await fetchProspectDetails(prospectStatuses);
      } else {
        console.log(`📋 [useProspectList] No prospects found`);
        setProspects([]);
      }

    } catch (err) {
      let errorMessage = 'Unknown error';
      
      // Check for network/fetch errors
      if (err instanceof TypeError) {
        const errMsg = err.message || String(err);
        if (errMsg.includes('fetch') || errMsg.includes('Failed to fetch') || errMsg.includes('NetworkError')) {
          // Network error - API server likely not running or CORS issue
          errorMessage = 'Failed to fetch - API server may be unavailable';
          console.error('❌ [useProspectList] Network error - check if API server is running at', API_BASE);
          console.error('❌ [useProspectList] Error details:', err);
        } else {
          errorMessage = errMsg;
          console.error('❌ [useProspectList] TypeError:', errMsg);
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
        console.error('❌ [useProspectList] Error fetching statuses:', errorMessage);
      } else {
        errorMessage = String(err);
        console.error('❌ [useProspectList] Unknown error:', err);
      }
      
      setError(errorMessage);
      setProspects([]);
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  // Fetch full use case details for prospects
  const fetchProspectDetails = async (prospectStatuses: UseCaseStatus[]) => {
    try {
      const response = await fetch(`${API_BASE}/use-cases-with-scores?clientId=${clientId}`);
      
      if (!response.ok) {
        console.warn(`⚠️ [useProspectList] API returned ${response.status}, trying to fetch individual use case details...`);
        
        // Try to fetch use case details individually as fallback
        const fallbackProspects: UseCaseMeta[] = [];
        for (const status of prospectStatuses) {
          try {
            const detailResponse = await fetch(`${API_BASE}/useCases/${status.use_case_id}`);
            if (detailResponse.ok) {
              const detail = await detailResponse.json();
              fallbackProspects.push({
                use_case_id: status.use_case_id,
                name: detail.name || detail.title || detail.use_case_name || `Use Case ${status.use_case_id}`,
                use_case_abr: detail.use_case_abr || detail.abbreviation || detail.short_name || null,
                status: status.status || 'prospect'
              });
            } else {
              // If individual fetch fails, use placeholder
              fallbackProspects.push({
                use_case_id: status.use_case_id,
                name: `Use Case ${status.use_case_id}`,
                status: status.status || 'prospect'
              });
            }
          } catch (err) {
            console.warn(`⚠️ [useProspectList] Failed to fetch detail for ${status.use_case_id}:`, err);
            fallbackProspects.push({
              use_case_id: status.use_case_id,
              name: `Use Case ${status.use_case_id}`,
              status: status.status || 'prospect'
            });
          }
        }
        
        setProspects(fallbackProspects);
        return;
      }

      const useCases = await response.json();
      console.log(`✅ [useProspectList] Fetched ${useCases.length} use cases with scores`);
      
      // Join statuses with use case metadata
      const prospectIds = new Set(prospectStatuses.map(s => s.use_case_id));
      console.log(`🔍 [useProspectList] Looking for prospect IDs:`, Array.from(prospectIds));
      console.log(`🔍 [useProspectList] Available use case IDs from API:`, useCases.map((uc: any) => uc.id || uc.use_case_id));
      
      // Create a map of use_case_id -> status for quick lookup
      const statusMap = new Map(prospectStatuses.map(s => [s.use_case_id, s.status]));
      
      // Filter and map prospects - try both uc.id and uc.use_case_id
      const prospectsWithDetails: UseCaseMeta[] = useCases
        .filter((uc: any) => {
          const ucId = uc.id || uc.use_case_id;
          const isProspect = prospectIds.has(ucId);
          if (isProspect) {
            console.log(`✅ [useProspectList] Matched prospect ID ${ucId} with name: ${uc.name || uc.title || 'N/A'}`);
          }
          return isProspect;
        })
        .map((uc: any, index: number) => {
          const ucId = uc.id || uc.use_case_id;
          // Log the raw API response for debugging (first 3 prospects to see structure)
          if (index < 3) {
            console.log(`📋 [useProspectList] Processing prospect ${ucId} from API:`, {
              id: uc.id,
              use_case_id: uc.use_case_id,
              matchedId: ucId,
              name: uc.name,
              use_case_abr: uc.use_case_abr,
              use_case_name: uc.use_case_name,
              title: uc.title,
              abbreviation: uc.abbreviation,
              short_name: uc.short_name,
              allFields: Object.keys(uc)
            });
          }
          
          // Try multiple possible field names for name and abbreviation
          const useCaseName = uc.name || uc.use_case_name || uc.title || null;
          const useCaseAbr = uc.use_case_abr || uc.abbreviation || uc.short_name || null;
          
          if (!useCaseName && index < 3) {
            console.warn(`⚠️ [useProspectList] Use case ${ucId} has no name field! Available fields:`, Object.keys(uc));
          }
          
          const mappedProspect = {
            use_case_id: ucId, // Use the matched ID
            // Use the actual name from API - try multiple field names
            name: useCaseName || `Use Case ${ucId}`,
            // Use use_case_abr directly from API (confirmed in schema as the correct field name)
            use_case_abr: useCaseAbr || null,
            business_value_score: uc.value_score,
            implementation_score: uc.implementation_score,
            category_name: uc.category_name || 'Uncategorized',
            annual_revenue_potential: uc.annual_revenue_potential,
            payback_months: uc.payback_months,
            confidence_level: uc.confidence_level,
            status: statusMap.get(ucId) || 'prospect' // Use status from status record
          };
          
          console.log(`✅ [useProspectList] Mapped prospect: ID=${mappedProspect.use_case_id}, Name="${mappedProspect.name}", Abbr="${mappedProspect.use_case_abr || 'N/A'}"`);
          
          return mappedProspect;
        });

      console.log(`✅ [useProspectList] Mapped ${prospectsWithDetails.length} prospects with details`);
      setProspects(prospectsWithDetails);

    } catch (err) {
      console.error('❌ [useProspectList] Error fetching prospect details:', err);
      // Fallback to basic prospect list - use status from status record
      setProspects(
        prospectStatuses.map(s => ({
          use_case_id: s.use_case_id,
          name: `Use Case ${s.use_case_id}`, // Fallback only when API fails
          status: s.status || 'prospect' // Use actual status from record
        }))
      );
    }
  };

  // Update status for a use case
  const updateStatus = async (useCaseId: number, status: string) => {
    if (!clientId) {
      console.error('Cannot update status: no clientId');
      return;
    }

    try {
      console.log(`🔄 [useProspectList] Updating status for use case ${useCaseId} to "${status}"...`);

      if (status === 'qualified' || status === '') {
        // Remove status (back to qualified/default)
        const response = await fetch(
          `${API_BASE}/usecases/${clientId}/${useCaseId}/status`,
          { method: 'DELETE' }
        );

        if (!response.ok) {
          throw new Error(`Failed to remove status: ${response.status}`);
        }
      } else {
        // Set new status
        const response = await fetch(
          `${API_BASE}/usecases/${clientId}/${useCaseId}/status`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status })
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to update status: ${response.status}`);
        }
      }

      console.log(`✅ [useProspectList] Status updated successfully`);
      
      // Refresh data
      await fetchStatuses();

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update status';
      console.error('❌ [useProspectList] Error updating status:', errorMessage);
      setError(errorMessage);
      throw err; // Re-throw so caller can handle
    }
  };

  // Fetch on mount and when clientId changes
  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  return {
    prospects,
    statuses,
    isLoading,
    error,
    updateStatus,
    refetch: fetchStatuses
  };
}

export type { UseCaseStatus, UseCaseMeta, ProspectListHook };